import { AccessRequest } from '@infuse/api/iam/access-request';
import {
  addRolesToOrganisationUser,
  addUsersToOrganisation,
  getOrganisationUser
} from '@infuse/api/iam/organisation';
import { addRolesToTenantUser, addUsersToTenant, getTenantUser } from '@infuse/api/iam/tenant';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import axios from 'axios';
import { useI18n } from 'vue-i18n';

export interface ApproveAccessRequestPayload {
  request: AccessRequest;
  roleIds: string[];
}

/**
 * Approve an access request by chaining add-users (if needed) + add-roles
 * against the org or tenant scope of the request. The backend's role-save
 * validator flips the matching PENDING request to APPROVED atomically.
 */
export const useApproveAccessRequest = () => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation({
    mutationFn: async ({ request, roleIds }: ApproveAccessRequestPayload) => {
      const orgId = request.targetOrganisationId;
      const tenantId = request.targetTenantId;
      const userId = request.requesterUserId;

      try {
        if (orgId) {
          const isMember = await probeMembership(() => getOrganisationUser({ id: orgId, userId }));
          if (!isMember) {
            await addUsersToOrganisation({ id: orgId }, { userIds: [userId] });
          }
          await addRolesToOrganisationUser({ id: orgId, userId }, { roleIds, replace: false });
        } else if (tenantId) {
          const isMember = await probeMembership(() => getTenantUser({ id: tenantId, userId }));
          if (!isMember) {
            await addUsersToTenant({ id: tenantId }, { userIds: [userId] });
          }
          await addRolesToTenantUser({ id: tenantId, userId }, { roleIds, replace: false });
        } else {
          throw new Error('Access request has neither targetOrganisationId nor targetTenantId');
        }

        notify({
          title: t('iam.access-requests.header.access-requests'),
          message: t('iam.access-requests.notify.approved')
        });
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.access-requests.header.access-requests'),
          message: getAxiosErrorMessage(e, t('iam.access-requests.notify.approve_failed'))
        });
        throw e;
      }
    },
    onSettled: async (_data, _err, { request }) => {
      await queryClient.invalidateQueries({ queryKey: ['access-requests'] });
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      if (request.targetOrganisationId) {
        await queryClient.invalidateQueries({ queryKey: ['organisations'] });
      }
      if (request.targetTenantId) {
        await queryClient.invalidateQueries({ queryKey: ['tenants'] });
      }
    }
  });

  return { ...mutation };
};

const probeMembership = async (fetch: () => Promise<unknown>): Promise<boolean> => {
  try {
    await fetch();
    return true;
  } catch (e) {
    if (axios.isAxiosError(e) && (e.response?.status === 404 || e.response?.status === 403)) {
      return false;
    }
    throw e;
  }
};
